import React, { useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ProductCell } from '../OrderDetails/components/ProductCell'
import { Table } from '../OrderDetails/components/Table'
import { SquareAmountCell } from '../OrderDetails/components/SquareAmountCell'
import { useOrderGroupDetailContext } from './context/OrderGroupDetailContext'
import { formatDate } from '../../lib/Util'
import { graphql, useFragment } from '../../gql'
import { Product, ProductCell_ProductFragmentFragment } from '../../gql/graphql'

export const GroupClearanceItems_OrderGroupFragment = graphql(`
  fragment GroupClearanceItems_OrderGroup on OrderGroup {
    id
    clearanceItems {
      id
      amount
      position
      expirationDate
      batch
      product {
        ean
        code
        ...ProductCell_ProductFragment
      }
    }
  }
`)

type ClearanceItemRow = {
  id: number
  code?: string
  ean?: string
  product?: GroupClearanceItems_OrderGroupFragment['clearanceItems'][number]['product']
  position?: string
  expirationDate?: string
  amount?: number
  batch?: string
}

const columnHelper = createColumnHelper<ClearanceItemRow>()

export const GroupClearanceItems: React.FC = () => {
  const { orderGroup } = useOrderGroupDetailContext()
  const group = useFragment(GroupClearanceItems_OrderGroupFragment, orderGroup)

  const clearanceItems = useMemo(() => {
    if (!group?.clearanceItems) return []
    return group.clearanceItems.map(item => ({
      id: item.id,
      product: item.product,
      code: item.product?.code,
      ean: item.product?.ean,
      expirationDate: item?.expirationDate,
      batch: item.batch,
      amount: item.amount,
      position: item.position,
    }))
  }, [group])

  const Columns = useMemo(
    () => [
      columnHelper.accessor('product', {
        header: 'Zboží',
        cell: ({ getValue, row }) =>
          getValue() ? <ProductCell product={getValue()} /> : <span className="text-gray-500">Neznámé zboží</span>,
      }),
      columnHelper.accessor('code', { header: 'Kód', size: 250 }),
      columnHelper.accessor('ean', { header: 'EAN', size: 180 }),
      columnHelper.accessor('expirationDate', { header: 'Expirace', size: 180 }),
      columnHelper.accessor('batch', { header: 'Sárže', size: 180 }),
      columnHelper.accessor('amount', {
        header: () => <div className="text-center">Ks.</div>,
        cell: ({ getValue }) => <SquareAmountCell>{getValue()}</SquareAmountCell>,
        size: 80,
      }),
      columnHelper.accessor('position', {
        header: 'Poz.',
        cell: ({ getValue }) => <div className="text-center">{getValue()}</div>,
        size: 100,
      }),
    ],
    [],
  )

  const columnVisibility = useMemo(
    () => ({
      ean: !clearanceItems.every(el => !el.ean),
      code: !clearanceItems.every(el => !el.code),
      expirationDate: !clearanceItems.every(el => !el.expirationDate),
      batch: !clearanceItems.every(el => !el.batch),
    }),
    [clearanceItems],
  )

  const tableState = useMemo(
    () => ({
      columnVisibility,
    }),
    [columnVisibility],
  )

  const table = useReactTable({
    columns: Columns,
    data: clearanceItems,
    getCoreRowModel: getCoreRowModel(),
    state: tableState,
    defaultColumn: {
      // @ts-ignore
      size: '1fr',
    },
  })

  return (
    <div className="h-full w-full flex flex-col">
      <h2 className="text-lg font-semibold mb-4">Položky objednávek ({clearanceItems.length})</h2>
      <div className="flex-1 overflow-auto w-full">
        <Table table={table} />
      </div>
    </div>
  )
}
